import React, { useContext } from 'react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import './style.css';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
import { WindowSizeContext } from '/src/App';
import { RiskOverviewContext } from '../../RiskOverview';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement  // Add this for doughnut chart
);

// Add this mapping near the top of your component
const incidentTypeMapping = {
    'R': 'Fire',
    'F': 'Flood',
    'M': 'Mudslide/Landslide',
    'W': 'Severe Storm',
    '5': 'Winter Weather',
    'T': 'Tornado',
    '2': 'Wind',
    'O': 'Severe Storm',
    'S': 'Snowstorm',
    'H': 'Hurricane',
    'E': 'Earthquake',
    'D': 'Drought',
    'C': 'Coastal Storm',
    'A': 'Tsunami',

};

// Add this mapping for declaration titles
const titleKeywordMapping = {
    'FIRE': 'Fire',
    'SEVERE': 'Severe Storm',
    'TORNADO': 'Tornado',
    'FLOOD': 'Flood',
    'LANDSLIDE': 'Mudslide/Landslide',
    'MUDSLIDE': 'Mudslide/Landslide',
    'STORM': 'Severe Storm',
    'HURRICANE': 'Hurricane',
    'WINTER': 'Winter Weather',
    'SNOW': 'Winter Weather',
    'EARTHQUAKE': 'Earthquake',
    'DROUGHT': 'Drought',
    'TSUNAMI': 'Tsunami'
};

const SeeMoreClimateDetails = ({ countyName }) => {
    const { geoData } = useContext(AreaDataContext);
    const { deviceType } = useContext(WindowSizeContext);

    const stateName = geoData?.state?.state_long || '';

    const propertyFipsFemaData = geoData?.fips?.fema_data?.detailed_records?.disaster_declarations || [];
    const otherFipsFemaData = geoData?.state?.fema_data?.county_level?.detailed_records?.disaster_declarations || [];
    const statewideFemaData = geoData?.state?.fema_data?.state_level?.detailed_records?.disaster_declarations || [];
    console.log('geoData', geoData);
    console.log('propertyFipsFemaData otherFipsFemaData statewideFemaData', propertyFipsFemaData, otherFipsFemaData, statewideFemaData);
    
    const { femaData } = useContext(RiskOverviewContext);
    const disasterDeclarations = femaData.detailed_records.disaster_declarations;

    const allDisasterDeclarations = [...propertyFipsFemaData, ...otherFipsFemaData];

    // Group disasters by year
    const disastersByYear = disasterDeclarations.reduce((acc, declaration) => {
        const year = declaration.year;
        acc[year] = (acc[year] || 0) + 1;
        return acc;
    }, {});

    // Modified incident type counting with enhanced mapping
    const incidentTypeCounts = allDisasterDeclarations.reduce((acc, declaration) => {
        if (declaration.designatedIncidentTypes && Array.isArray(declaration.designatedIncidentTypes)) {
            // Use designatedIncidentTypes if available
            declaration.designatedIncidentTypes.forEach(type => {
                const readableName = incidentTypeMapping[type] || type;
                acc[readableName] = (acc[readableName] || 0) + 1;
            });
        } else if (declaration.declarationTitle) {
            // Check for keywords in the title
            const title = declaration.declarationTitle.toUpperCase();
            const matchedTypes = Object.entries(titleKeywordMapping)
                .filter(([keyword]) => title.includes(keyword))
                .map(([, type]) => type);
            
            // Only count if we found matching keywords
            if (matchedTypes.length > 0) {
                // Remove duplicates (in case multiple keywords map to same type)
                [...new Set(matchedTypes)].forEach(type => {
                    acc[type] = (acc[type] || 0) + 1;
                });
            }
        }
        return acc;
    }, {});

    // Sort the counts from largest to smallest
    const sortedIncidentTypes = Object.entries(incidentTypeCounts)
        .sort(([, a], [, b]) => b - a);

    // Convert incident type data for doughnut chart with sorted data
    const incidentTypeData = {
        labels: sortedIncidentTypes.map(([label]) => label),
        datasets: [{
            data: sortedIncidentTypes.map(([, count]) => count),
            backgroundColor: [
                '#115453',  // Darkest teal
                '#166E6D',
                '#1B8180',
                '#209493',
                '#25ADAC',
                '#2AC6C5',
                '#2FD9D7',
                '#34ECEB',
                '#39FFFF',  // Lightest teal
                '#45FFFF'   // Extra light teal
            ],
            borderColor: 'white',
            borderWidth: 2
        }]
    };

    // Get current year
    const currentYear = new Date().getFullYear();
    
    // Create array of last 10 years with 0 counts for property FIPS
    const propertyFipsYearlyData = Array.from({ length: 10 }, (_, index) => ({
        year: currentYear - 9 + index,
        count: 0
    }));

    // Create array of last 10 years with 0 counts for other FIPS
    const otherFipsYearlyData = Array.from({ length: 10 }, (_, index) => ({
        year: currentYear - 9 + index,
        count: 0
    }));

    // Fill in the actual disaster counts for property FIPS
    propertyFipsFemaData.forEach(declaration => {
        const yearIndex = propertyFipsYearlyData.findIndex(data => data.year === declaration.year);
        if (yearIndex !== -1) {
            propertyFipsYearlyData[yearIndex].count++;
        }
    });

    // Fill in the actual disaster counts for other FIPS
    otherFipsFemaData.forEach(declaration => {
        const yearIndex = otherFipsYearlyData.findIndex(data => data.year === declaration.year);
        if (yearIndex !== -1) {
            otherFipsYearlyData[yearIndex].count++;
        }
    });

    // Find the maximum count across both datasets
    const maxCount = Math.max(
        ...propertyFipsYearlyData.map(item => item.count),
        ...otherFipsYearlyData.map(item => item.count)
    );

    // Prepare chart data with both lines
    const chartData = {
        labels: propertyFipsYearlyData.map(item => item.year),
        datasets: [
            {
                label: `${countyName}`,
                data: propertyFipsYearlyData.map(item => item.count),
                borderColor: `#f27e7e`,
                backgroundColor: `#f27e7e1c`,
                tension: 0.3,
                fill: true
            },
            {
                label: `Other ${stateName} Counties`,
                data: otherFipsYearlyData.map(item => item.count),
                borderColor: `#7e9cf2`,
                backgroundColor: `#7e9cf21c`,
                tension: 0.3,
                fill: true
            }
        ]
    };

    // Update the line chart options to show legend since we now have multiple lines
    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true, // Changed to true to show the legend
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `Disasters: ${context.raw}`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(200, 200, 200, 0.2)'
                },
                ticks: {
                    stepSize: 1,
                    precision: 0
                },
                title: {
                    display: true,
                    text: 'Number of Disasters'
                },
                max: maxCount + 1  // Set max to highest count plus 1
            },
            x: {
                grid: {
                    color: 'rgba(200, 200, 200, 0.2)'
                },
                title: {
                    display: true,
                    text: 'Year'
                }
            }
        },
        elements: {
            point: {
                radius: 0,
                hoverRadius: 5,
                display: false
            },
            line: {
                tension: 0.3
            }
        }
    };

    // Doughnut chart options
    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',  // Move legend to bottom
            },
            datalabels: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const value = context.raw;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `   ${value} Events (${percentage}%)`;
                    }
                }
            }
        }
    };

    return (
        <div className={`see-more-climate-details ${deviceType}`}>
            <h3 className="disaster-chart-title">FEMA Declared Climate Disasters in {stateName} Over the Last 10 Years</h3>
            <div className={`disaster-charts-container ${deviceType}`}>
                <div className="disaster-chart-container">
                    <Line data={chartData} options={lineChartOptions} />
                </div>
                <div className="disaster-type-container">
                    {/* <h3>Disaster Types Distribution</h3> */}
                    <Doughnut data={incidentTypeData} options={doughnutOptions} />
                </div>
            </div>
        </div>
    );
};

export default SeeMoreClimateDetails;